
import React from 'react'
import { Control, useFormState } from 'react-hook-form'

import { Button, Tooltip } from '@repo/ui'

import { submitDisabledReason } from './disabledReason'

type ButtonComponentProps = React.ComponentProps<typeof Button>

export interface FormSubmitButtonProps extends Omit<ButtonComponentProps, 'disabled'> {
  /**
   * The react-hook-form `control` to read dirty/error state from. `useFormState`
   * falls back to the nearest `FormProvider` context when this is omitted (RHF's
   * own default) — pass it explicitly for a form that isn't wrapped in one, which
   * is the common case in this codebase (`useForm()` used directly, `control`
   * threaded to `Controller` by hand rather than via context).
   */
  control?: Control<any>
  /**
   * Create-mode forms start empty and legitimately have nothing to be dirty
   * against — an unconditional dirty gate would leave a create form's submit
   * permanently disabled. Pass `false` to skip the dirty check, mirroring
   * `ProductEditPage`'s `page === 'edit'` split. Defaults to `true`.
   */
  requireDirty?: boolean
  /**
   * An extra condition that also disables the button — a mutation in flight,
   * a missing permission, etc. Composed with the dirty/error gate below rather
   * than replacing it.
   */
  disabled?: boolean
  /**
   * Why the CALLER's own `disabled` is set — "Saving…", "You need the Manage
   * company permission". Shown as the tooltip when `disabled` is what blocked the
   * button.
   *
   * Only the caller knows this. The dirty and error cases are derived here and need
   * no prop. If `disabled` is passed without a reason the button still disables, but
   * silently — working-conventions §46 asks for a reason on every disabled control,
   * and this is the one case this component cannot supply one for.
   */
  disabledReason?: string
  /**
   * Display names for form fields, keyed by the RHF field name, used when naming
   * what failed validation. Without it the field name is humanised
   * (`scan_media_url` -> "Scan media url"), which is usually right and occasionally
   * clumsy; pass this where the form's labels differ from its field names.
   */
  fieldLabels?: Record<string, string>
}

/**
 * A form's Save/submit button, disabled until the form is dirty and disabled
 * while any of its fields has a validation error — `isDirty && !hasErrors`.
 *
 * Codifies the working-conventions rule ("a form's Save/submit button is
 * disabled until the form is dirty — an enabled Save on an untouched form
 * promises an action that would do nothing") so a NEW form gets this by
 * construction instead of being the next one to ship an always-enabled Save.
 *
 * Cancel is deliberately NOT this component. Cancel stays enabled always — on
 * a pristine form it means "leave", not "discard", and disabling it strands
 * a user with no way out but the breadcrumb.
 *
 * VRG-776: it also SAYS WHY it is disabled, on hover. The three causes are
 * indistinguishable on screen — a greyed Save looks identical whether nothing has
 * changed, a field three sections up is invalid, or a save is already in flight —
 * and the second of those is a genuine dead end on a form long enough to need a
 * table of contents.
 *
 * The reason belongs HERE rather than at each call site because this is the only
 * place that knows which condition fired; a per-form tooltip would be an opt-in
 * convention, which this repo has twice measured as a thing roughly half of call
 * sites forget (working-conventions §42, §54).
 */
export const FormSubmitButton = ({
  control,
  requireDirty = true,
  disabled,
  disabledReason,
  fieldLabels,
  children,
  ...rest
}: FormSubmitButtonProps) => {
  const { isDirty, errors } = useFormState({ control })
  const errorNames = Object.keys(errors)
  const hasErrors = errorNames.length > 0
  const isBlocked = (requireDirty && !isDirty) || hasErrors || !!disabled

  // VRG-776 — say WHY, because the three causes are indistinguishable on screen.
  // The precedence between them, and the reasoning for it, lives in `disabledReason`.
  const reason = submitDisabledReason({
    blocked: isBlocked,
    callerDisabled: disabled,
    callerReason: disabledReason,
    errorNames,
    labels: fieldLabels,
  })

  const button = (
    <Button disabled={isBlocked} {...rest}>
      {children}
    </Button>
  )

  if (!reason) return button

  /*
   * A disabled MUI button sets `pointer-events: none`, so it fires no hover events
   * and a Tooltip wrapped straight around it NEVER SHOWS — the single reason this
   * pattern is usually attempted and quietly abandoned. The documented fix is an
   * interactive wrapper element, which still receives the pointer.
   *
   * `inline-flex` rather than the default inline `span`: the button is a flex item
   * in every call site here (a Cancel/Save pair in a `Stack direction="row"`), and
   * an inline wrapper would not stretch to it, collapsing the button's box. As a
   * flex item the wrapper occupies exactly the slot the button did, so the group's
   * gap (§29) is unchanged.
   */
  return (
    <Tooltip title={reason}>
      <span style={{ display: 'inline-flex' }}>{button}</span>
    </Tooltip>
  )
}
